import React, { useState, Fragment } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import {
  ArrowLeftIcon,
  UserIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  ChevronUpDownIcon
} from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  displayName: string | null;
  email: string;
  role: any;
  roleId?: string;
  managerId?: string | null;
  Manager?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  roles: string[];
  status: string;
  title: string | null;
  department: { id: string; name: string } | null;
  active: boolean;
  lockedOut: boolean;
  businessPhone: string | null;
  mobilePhone: string | null;
  photo: string | null;
  createdAt: string;
  lastLogin: string | null;
}

interface ManagerOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  title: string | null;
}

interface RoleOption {
  id: string;
  name: string;
  roleType: string;
  description?: string;
}

const jobTitleOptions = [
  { value: 'Manager', label: 'Manager' },
  { value: 'Team Lead', label: 'Team Lead' },
  { value: 'Team Member', label: 'Team Member' }
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' }
];

const UserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const usersListSearch = (location.state as { usersListSearch?: string } | null)?.usersListSearch;
  const usersListPath =
    usersListSearch !== undefined ? `/users${usersListSearch}` : '/users';
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<User>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { canUpdateEntity } = useUserPermissions();

  // Search query states for comboboxes
  const [roleQuery, setRoleQuery] = useState('');
  const [managerQuery, setManagerQuery] = useState('');
  const [titleQuery, setTitleQuery] = useState('');
  const [statusQuery, setStatusQuery] = useState('');

  const { data: user, isLoading, error } = useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      const response = await api.get(`/users/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  // Get all users for navigation
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/users');
      return response.data;
    },
  });

  // Get all available roles (system + custom) - increased limit
  const { data: rolesData } = useQuery({
    queryKey: ['roles-all'],
    queryFn: async () => {
      const response = await api.get('/roles', {
        params: { limit: 1000, active: true }
      });
      return response.data;
    },
  });

  // Get all users who can be managers (active users) - increased limit
  const { data: managersData } = useQuery({
    queryKey: ['potentialManagers'],
    queryFn: async () => {
      const response = await api.get('/users', {
        params: { limit: 1000, active: true }
      });
      return response.data;
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async (data: Partial<User>) => {
      const response = await api.put(`/users/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User updated successfully');
      setIsEditing(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update user');
    },
  });

  React.useEffect(() => {
    if (user) {
      setFormData({
        ...user,
        roleId: user.role?.id || user.roleId,
        managerId: user.managerId || ''
      });
    }
  }, [user]);

  // Prevent users without edit permissions from being in edit mode
  React.useEffect(() => {
    if (user && !canUpdateEntity('users') && isEditing) {
      setIsEditing(false);
    }
  }, [user, canUpdateEntity, isEditing]);

  // Navigation logic
  const users = usersData?.users || [];
  const currentIndex = users.findIndex((u: any) => u.id === id);
  const previousUser = currentIndex > 0 ? users[currentIndex - 1] : null;
  const nextUser = currentIndex < users.length - 1 ? users[currentIndex + 1] : null;

  const navigateToUser = (userId: string) => {
    if (isEditing) {
      const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmLeave) return;
    }
    navigate(`/users/${userId}`, { state: location.state });
  };

  const validateField = (name: string, value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;

    switch (name) {
      case 'email':
        if (value && !emailRegex.test(value)) {
          return 'Invalid email format';
        }
        break;
      case 'businessPhone':
      case 'mobilePhone':
        if (value) {
          const cleanedPhone = value.replace(/\s+/g, '');
          if (!phoneRegex.test(cleanedPhone)) {
            return 'Invalid phone number format';
          }
          // Check minimum 10 digits (excluding country code +)
          const digitCount = cleanedPhone.replace(/[^\d]/g, '').length;
          if (digitCount < 10) {
            return 'Phone number must have at least 10 digits';
          }
        }
        break;
    }
    return '';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));

    // Inline validation for email and phone
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Check for existing inline errors
    if (errors.email || errors.businessPhone || errors.mobilePhone) {
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      updateUserMutation.mutate(formData);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        ...user,
        roleId: user.role?.id || user.roleId,
        managerId: user.managerId || ''
      });
    }
    setErrors({});
    setIsEditing(false);
  };

  // Filter to show only SYSTEM and CUSTOM roles (exclude GOD and CUSTOMER)
  const availableRoles: RoleOption[] = (rolesData?.roles || []).filter((role: any) =>
    role.roleType === 'SYSTEM' || role.roleType === 'CUSTOM'
  );

  // Filter managers (users with Manager or Team Lead title, excluding current user)
  const availableManagers: ManagerOption[] = (managersData?.users || [])
    .filter((u: ManagerOption) => u.id !== id && (u.title === 'Manager' || u.title === 'Team Lead'));

  // Get selected role
  const getSelectedRole = () => {
    return availableRoles.find((r: RoleOption) => r.id === formData.roleId);
  };

  if (isLoading) return <div className="p-4">Loading user details...</div>;
  if (error) return <div className="p-4 text-red-600">Error loading user details</div>;
  if (!user) return <div className="p-4">User not found</div>;

  // Check if user is super admin (cannot be edited) or if current user doesn't have update permission
  const isSuperAdmin = (user.role as any)?.name === 'super_admin' || user.role?.roleType === 'GOD';
  const canEditUser = canUpdateEntity('users') && !isSuperAdmin;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate(usersListPath)}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Users
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {user.photo ? (
                    <img
                      className="h-12 w-12 rounded-full"
                      src={user.photo}
                      alt={user.name}
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-gray-500" />
                    </div>
                  )}
                </div>
                <div className="ml-4">
                  <h1 className="text-xl font-semibold text-gray-900">{user.firstName} {user.lastName}</h1>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {!isEditing ? (
                  canEditUser && (
                    <div className="flex flex-col items-end">
                      <button
                        onClick={() => setIsEditing(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Edit User
                      </button>
                    </div>
                  )
                ) : (
                  <div className="space-x-2">
                    <button
                      onClick={handleSubmit}
                      disabled={updateUserMutation.isPending}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={handleCancel}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {/* Row 1: First Name and Last Name */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      name="firstName"
                      id="firstName"
                      value={formData.firstName || ''}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        !isEditing ? 'border-gray-300 bg-gray-100 text-gray-600' : 'border-gray-300'
                      }`}
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      name="lastName"
                      id="lastName"
                      value={formData.lastName || ''}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        !isEditing ? 'border-gray-300 bg-gray-100 text-gray-600' : 'border-gray-300'
                      }`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Display Name */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      name="displayName"
                      id="displayName"
                      value={formData.displayName || ''}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        !isEditing ? 'border-gray-300 bg-gray-100 text-gray-600' : 'border-gray-300'
                      }`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Job Title and Email Address */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                      Job Title
                    </label>
                  </div>
                  <div className="col-span-9">
                    {isEditing ? (
                      <Combobox
                        value={formData.title || ''}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, title: value || '' }));
                        }}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            displayValue={(value: string) => {
                              const option = jobTitleOptions.find(o => o.value === value);
                              return option ? option.label : '';
                            }}
                            onChange={(e) => setTitleQuery(e.target.value)}
                            placeholder="Select Job Title"
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setTitleQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {jobTitleOptions
                                .filter(option => option.label.toLowerCase().includes(titleQuery.toLowerCase()))
                                .map((option) => (
                                  <Combobox.Option
                                    key={option.value}
                                    value={option.value}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                          {option.label}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                            active ? 'text-white' : 'text-indigo-600'
                                          }`}>
                                            <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                    ) : (
                      <input
                        type="text"
                        value={jobTitleOptions.find(o => o.value === formData.title)?.label || formData.title || ''}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={user.email}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Business Phone and Mobile Phone */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="businessPhone" className="block text-sm font-medium text-gray-700 mb-1">
                      Business Phone
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="tel"
                      name="businessPhone"
                      id="businessPhone"
                      value={formData.businessPhone || ''}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        !isEditing
                          ? 'border-gray-300 bg-gray-100 text-gray-600'
                          : errors.businessPhone
                            ? 'border-red-500'
                            : 'border-gray-300'
                      }`}
                    />
                    {errors.businessPhone && isEditing && (
                      <p className="mt-1 text-xs text-red-600">{errors.businessPhone}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="mobilePhone" className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Phone
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="tel"
                      name="mobilePhone"
                      id="mobilePhone"
                      value={formData.mobilePhone || ''}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        !isEditing
                          ? 'border-gray-300 bg-gray-100 text-gray-600'
                          : errors.mobilePhone
                            ? 'border-red-500'
                            : 'border-gray-300'
                      }`}
                    />
                    {errors.mobilePhone && isEditing && (
                      <p className="mt-1 text-xs text-red-600">{errors.mobilePhone}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Role and Manager */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="roleId" className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                  </div>
                  <div className="col-span-9">
                    {isEditing ? (
                      <Combobox
                        value={formData.roleId || ''}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, roleId: value || '' }));
                        }}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            displayValue={(roleId: string) => {
                              const role = availableRoles.find((r: RoleOption) => r.id === roleId);
                              if (!role) return '';
                              const displayName = role.name.charAt(0).toUpperCase() + role.name.slice(1).replace('_', ' ');
                              return role.roleType === 'CUSTOM' ? `${displayName} ⭐` : displayName;
                            }}
                            onChange={(e) => setRoleQuery(e.target.value)}
                            placeholder="Search roles..."
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setRoleQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {availableRoles.length === 0 ? (
                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                  No roles found.
                                </div>
                              ) : (
                                availableRoles
                                  .filter((role: RoleOption) => {
                                    const displayName = role.name.charAt(0).toUpperCase() + role.name.slice(1).replace('_', ' ');
                                    return displayName.toLowerCase().includes(roleQuery.toLowerCase()) ||
                                           (role.description && role.description.toLowerCase().includes(roleQuery.toLowerCase()));
                                  })
                                  .map((role: RoleOption) => (
                                    <Combobox.Option
                                      key={role.id}
                                      value={role.id}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {role.name.charAt(0).toUpperCase() + role.name.slice(1).replace('_', ' ')}
                                            {role.roleType === 'CUSTOM' && ' ⭐'}
                                          </span>
                                          {role.description && (
                                            <span className={`block truncate text-xs ${active ? 'text-indigo-200' : 'text-gray-500'}`}>
                                              {role.description}
                                            </span>
                                          )}
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                              active ? 'text-white' : 'text-indigo-600'
                                            }`}>
                                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))
                              )}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                    ) : (
                      <input
                        type="text"
                        value={(() => {
                          const role = availableRoles.find((r: RoleOption) => r.id === formData.roleId);
                          if (!role) return '';
                          const displayName = role.name.charAt(0).toUpperCase() + role.name.slice(1).replace('_', ' ');
                          return role.roleType === 'CUSTOM' ? `${displayName} ⭐` : displayName;
                        })()}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    )}
                    {isEditing && getSelectedRole()?.roleType === 'CUSTOM' && (
                      <p className="mt-1 text-xs text-purple-600">⭐ Custom role with specific permissions</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="managerId" className="block text-sm font-medium text-gray-700 mb-1">
                      Manager
                    </label>
                  </div>
                  <div className="col-span-9">
                    {isEditing ? (
                      <Combobox
                        value={formData.managerId || ''}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, managerId: value || '' }));
                        }}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            displayValue={(managerId: string) => {
                              if (!managerId) return '';
                              const manager = availableManagers.find((m: ManagerOption) => m.id === managerId);
                              return manager ? `${manager.firstName} ${manager.lastName}` : '';
                            }}
                            onChange={(e) => setManagerQuery(e.target.value)}
                            placeholder="Search managers..."
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setManagerQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {/* No Manager option */}
                              <Combobox.Option
                                value=""
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'} italic`}>
                                      No Manager
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-indigo-600'
                                      }`}>
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                              {availableManagers
                                .filter((manager: ManagerOption) => {
                                  const searchStr = `${manager.firstName} ${manager.lastName} ${manager.email}`.toLowerCase();
                                  return searchStr.includes(managerQuery.toLowerCase());
                                })
                                .map((manager: ManagerOption) => (
                                  <Combobox.Option
                                    key={manager.id}
                                    value={manager.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                          {manager.firstName} {manager.lastName}
                                        </span>
                                        <span className={`block truncate text-xs ${active ? 'text-indigo-200' : 'text-gray-500'}`}>
                                          {manager.email} {manager.title && `• ${manager.title}`}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                            active ? 'text-white' : 'text-indigo-600'
                                          }`}>
                                            <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                    ) : (
                      <input
                        type="text"
                        value={(() => {
                          if (!formData.managerId) return 'No Manager';
                          const manager = availableManagers.find((m: ManagerOption) => m.id === formData.managerId);
                          return manager ? `${manager.firstName} ${manager.lastName}` : 'No Manager';
                        })()}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 5: Status */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                  </div>
                  <div className="col-span-9">
                    {isEditing ? (
                      <Combobox
                        value={formData.status || 'active'}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, status: value || 'active' }));
                        }}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            displayValue={(value: string) => {
                              const option = statusOptions.find(o => o.value === value);
                              return option ? option.label : '';
                            }}
                            onChange={(e) => setStatusQuery(e.target.value)}
                            placeholder="Select Status"
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setStatusQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {statusOptions
                                .filter(option => option.label.toLowerCase().includes(statusQuery.toLowerCase()))
                                .map((option) => (
                                  <Combobox.Option
                                    key={option.value}
                                    value={option.value}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                          {option.label}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                            active ? 'text-white' : 'text-indigo-600'
                                          }`}>
                                            <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                    ) : (
                      <input
                        type="text"
                        value={statusOptions.find(o => o.value === formData.status)?.label || formData.status || ''}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6"></div>
            </div>

            {/* Row 6: Active and Locked Out checkboxes */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      name="active"
                      id="active"
                      checked={formData.active || false}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded disabled:bg-gray-100"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="active" className="font-medium text-gray-700">
                      Active
                    </label>
                    <p className="text-gray-500">User can access the system</p>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      name="lockedOut"
                      id="lockedOut"
                      checked={formData.lockedOut || false}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded disabled:bg-gray-100"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="lockedOut" className="font-medium text-gray-700">
                      Locked Out
                    </label>
                    <p className="text-gray-500">User is locked out of the system</p>
                  </div>
                </div>
              </div>
            </div>


            {user.department && (
              <div className="grid grid-cols-12 gap-6 mb-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Department
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={user.department.name}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

          </form>
        </div>

        {/* Pagination Navigation */}
        {users.length > 0 && (
          <div className="mt-6 bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => previousUser && navigateToUser(previousUser.id)}
                  disabled={!previousUser}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => nextUser && navigateToUser(nextUser.id)}
                  disabled={!nextUser}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing user{' '}
                    <span className="font-medium">{currentIndex + 1}</span> of{' '}
                    <span className="font-medium">{users.length}</span>
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => previousUser && navigateToUser(previousUser.id)}
                      disabled={!previousUser}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
                      {currentIndex + 1} of {users.length}
                    </span>
                    <button
                      onClick={() => nextUser && navigateToUser(nextUser.id)}
                      disabled={!nextUser}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetail;
